import { useMemo, type ReactNode, type RefObject } from 'react'
import { ChartCard, TimeSeriesChart } from '../../components/ui'
import type { ChartTableData, EChartHandle, TimeSeriesEvent, TimeSeriesKind, TimeSeriesSeries } from '../../components/ui'
import { formatDate, formatDayMonth } from '../analytics/shared/format'
import { PIM_EVENT_LABELS } from '../analytics/shared/normalize'
import { explorerErrorText } from '../metrics-explorer/api'
import { errorKind, hasValues, type GroupState } from './profileModel'

/** Подписи типов событий: типы ПИМ и типы, которые отдаёт ряд продаж («акция», «фото или название»). */
export const PROFILE_EVENT_LABELS: Record<string, string> = {
  ...PIM_EVENT_LABELS,
  promo: 'Акция',
  content: 'Смена фото или названия',
}

/** Акции рисуются полосой у оси, а не вертикалями: их много. */
export const PROFILE_BAND_EVENTS = ['promo', 'promo_start', 'promo_end']

export const CHART_HEIGHT = 220

interface ProfileChartProps {
  title: string
  subtitle?: ReactNode
  actions?: ReactNode
  group: GroupState
  days: readonly string[]
  series: TimeSeriesSeries[]
  kind?: TimeSeriesKind
  fromZero?: boolean
  valueFormat: (value: number) => string
  axisFormat?: (value: number) => string
  events: TimeSeriesEvent[]
  chartRef: RefObject<EChartHandle | null>
  exportName: string
  /** Что написать, если данных за период нет совсем (например, у Ozon нет воронки). */
  emptyDescription: string
  /** Что написать, если у пользователя нет права на этот блок. */
  forbiddenTitle?: string
  onRetry?: () => void
}

/**
 * Один график профиля: общая ось дней периода, события ПИМ отметками, таблица-двойник с выгрузкой CSV.
 * Нет права — пояснение с текстом ответа, ошибка — сообщение с повтором, нет значений — «данных нет» (не ноль).
 */
export function ProfileChart({ title, subtitle, actions, group, days, series, kind = 'line', fromZero, valueFormat, axisFormat, events, chartRef, exportName, emptyDescription, forbiddenTitle, onRetry }: ProfileChartProps) {
  const dates = useMemo(() => [...days], [days])
  const forbidden = group.error ? errorKind(group.error) === 'forbidden' : false
  const table = useMemo<ChartTableData>(
    () => ({
      columns: [
        { key: 'date', label: 'День', format: (value) => formatDate(String(value ?? '')) },
        ...series.map((item) => ({ key: item.id, label: item.name, align: 'right' as const, format: (value: unknown) => (typeof value === 'number' ? valueFormat(value) : '—') })),
      ],
      rows: dates.map((date, index) => ({ date, ...Object.fromEntries(series.map((item) => [item.id, item.values[index] ?? null])) })),
    }),
    [dates, series, valueFormat],
  )
  const empty = forbidden
    ? { title: forbiddenTitle ?? 'Нет доступа', description: explorerErrorText(group.error) }
    : group.data && !series.some((item) => hasValues(item.values))
      ? { title: 'Данных за период нет', description: emptyDescription }
      : null

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      actions={actions}
      table={empty ? null : table}
      chartRef={chartRef}
      exportName={exportName}
      loading={!group.data && group.loading}
      error={!forbidden && group.error ? explorerErrorText(group.error) : null}
      onRetry={group.error && !forbidden ? onRetry : undefined}
      empty={empty}
      height={CHART_HEIGHT}
    >
      <TimeSeriesChart
        ref={chartRef}
        dates={dates}
        series={series}
        kind={kind}
        events={events}
        eventTypeLabels={PROFILE_EVENT_LABELS}
        bandEventTypes={PROFILE_BAND_EVENTS}
        valueFormat={valueFormat}
        axisFormat={axisFormat}
        dateFormat={formatDayMonth}
        tooltipDateFormat={(date) => formatDate(date)}
        fromZero={fromZero}
        zoom={false}
        loading={group.loading}
        height={CHART_HEIGHT}
        ariaLabel={title}
      />
    </ChartCard>
  )
}
